import { AbstractControl, ValidatorFn } from '@angular/forms';
import { isNullOrUndefined } from '@pineapplelab/util';
import { daysOfWeek } from '@app/enums/daysOfWeek.enum';

export type DateRangePreset = 'lastWeek' | 'currentMonth' | 'lastMonth' | 'currentYear' | 'lastYear';

/**
 * Returns { start, end } for a named date-range preset, or null for unknown types.
 * Shared by any component that offers preset date-range shortcuts.
 */
export function getPresetDateRange(type: string): { start: Date; end: Date } | null {
  const now = new Date();
  switch (type as DateRangePreset) {
    case 'lastWeek': {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay() - 6);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case 'currentMonth':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
      };
    case 'lastMonth':
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 0),
      };
    case 'currentYear':
      return {
        start: new Date(now.getFullYear(), 0, 1),
        end: new Date(),
      };
    case 'lastYear':
      return {
        start: new Date(now.getFullYear() - 1, 0, 1),
        end: new Date(now.getFullYear(), 0, 0),
      };
    default:
      return null;
  }
}

export function dateRangeValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const start = control.get('start');
    const end = control.get('end');

    if (!start?.value || !end?.value) {
      end.setErrors({ required: true });
      return { required: true };
    }

    if (start.value > end.value) {
      end.setErrors({ dateOrder: true });
      return { dateOrder: true };
    } else {
      end.setErrors(null);
    }

    return null;
  };
}

export function countDaysBetween(startDate: Date, endDate: Date, countWeekends: boolean = true, excludeDates: Date[] = []): number {
  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  let currentDate = new Date(startDate);
  let totalDays = 0;

  while (currentDate <= endDate) {
    const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
    const isExcluded = excludeDates.some((date) => date.toDateString() === currentDate.toDateString());

    if ((countWeekends || !isWeekend) && !isExcluded) {
      totalDays++;
    }

    currentDate = new Date(currentDate.getTime() + millisecondsPerDay);
  }

  return totalDays;
}

export function splitDateRangeByYear(startDate: Date, endDate: Date): { start: Date; end: Date }[] {
  const ranges = [];

  let currentStart = new Date(startDate);
  let currentYear = currentStart.getFullYear();

  while (currentStart <= endDate) {
    const yearEnd = new Date(currentYear, 11, 31);

    const currentEnd = endDate < yearEnd ? endDate : yearEnd;

    ranges.push({ start: new Date(currentStart), end: new Date(currentEnd) });

    currentYear++;
    currentStart = new Date(currentYear, 0, 1);
  }

  return ranges;
}

export function getCalendarYear(startDate: Date, endDate: Date): number {
  const startDateYear = startDate.getFullYear();
  const endDateYear = endDate.getFullYear();
  if (startDateYear === endDateYear) {
    return startDateYear;
  }
  if (startDate.getDate() === 1) {
    return startDateYear;
  }
  if (startDate.getDate() >= 25 && endDate.getDate() <= 10) {
    return startDateYear + 1;
  }
  if (startDate.getDate() >= 25) {
    return endDateYear;
  }
  return startDateYear;
}

export function getCalendarMonth(startDate: Date, endDate: Date): number {
  const startDateMonth = startDate.getMonth();
  const endDateMonth = endDate.getMonth();
  if (startDateMonth === endDateMonth) {
    return startDateMonth;
  }
  if (startDate.getDate() === 1) {
    return startDateMonth;
  }
  if (startDate.getDate() >= 25 && endDate.getDate() <= 10) {
    return startDateMonth === 11 ? 0 : startDateMonth + 1;
  }
  if (startDate.getDate() >= 25) {
    return endDateMonth;
  }
  return startDateMonth;
}

export function getCalendarYearEvent(dateStart: Date, dateEnd: Date, eventDate: Date): number {
  const eventMonth = eventDate.getMonth();
  const eventDay = eventDate.getDate();

  for (let year = dateStart.getFullYear(); year <= dateEnd.getFullYear(); year++) {
    const testDate = new Date(year, eventMonth, eventDay);
    if (testDate >= dateStart && testDate <= dateEnd) {
      return year;
    }
  }

  return dateStart.getFullYear();
}

export function formatDateToString(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();

  return `${day}-${month}-${year}`;
}

export function getDatesBetween(startDate: Date, endDate: Date): Date[] {
  const dates: Date[] = [];

  let currentDate = new Date(Date.UTC(
    startDate.getUTCFullYear(),
    startDate.getUTCMonth(),
    startDate.getUTCDate()
  ));

  const finalDate = new Date(Date.UTC(
    endDate.getUTCFullYear(),
    endDate.getUTCMonth(),
    endDate.getUTCDate()
  ));

  while (currentDate <= finalDate) {
    dates.push(new Date(currentDate.getTime()));
    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
  }

  return dates;
}

export function snakeToCamel(snakeCaseString: string): string {
  if (isNullOrUndefined(snakeCaseString)) {
    return snakeCaseString;
  }
  return snakeCaseString
    .toLowerCase()
    .split('_')
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');
}

export function camelToSnake(camelCaseString: string): string {
  if (isNullOrUndefined(camelCaseString)) {
    return camelCaseString;
  }
  return camelCaseString
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase();
}

/** Lowercased, trimmed copy of a name — used to populate `nameSearch` mirror fields for case-insensitive Firestore prefix search. */
export function toNameSearch(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase();
}


export function getYearsBetweenDates(startDate: Date, endDate: Date): number[] {
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  if (startYear > endYear) {
    return [];
  }

  const years: number[] = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }

  return years;
}

export function getMonthsBetweenDates(startDate: Date, endDate: Date): number[] {
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1;
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;

  if (startDate > endDate) {
    return [];
  }

  const months: number[] = [];
  for (let year = startYear; year <= endYear; year++) {
    const firstMonth = year === startYear ? startMonth : 1;
    const lastMonth = year === endYear ? endMonth : 12;
    for (let month = firstMonth; month <= lastMonth; month++) {
      months.push(month);
    }
  }

  return months;
}

export function filterDatesInRange(datesArray: string[], start: any, end: any, includeWeekends = { saturday: false, sunday: false }) {
  const filteredDays = datesArray.filter(dateStr => {
    const date = parseDate(dateStr);
    const dayOfWeek = date.getDay();
    if (date < start || date > end) {
      return false;
    }
    if (!includeWeekends.saturday && dayOfWeek === daysOfWeek.SATURDAY) {
      return false;
    }
    if (!includeWeekends.sunday && dayOfWeek === daysOfWeek.SUNDAY) {
      return false;
    }

    return true;
  });

  return filteredDays;
}

function parseDate(dateStr: string): Date {
  const [day, month, year] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day); // Month is zero-based in JS Dates
}

/**
 * Canonical Firestore Timestamp → JS Date converter.
 *
 * Handles every shape a Timestamp can arrive in after going through HTTP:
 *  - firebase.firestore.Timestamp instance  → .toDate()
 *  - Client SDK / plain JSON  { seconds }   → new Date(seconds * 1000)
 *  - Admin SDK / HTTP         { _seconds }  → new Date(_seconds * 1000)
 *  - After snakeToCamelCase mangling        { Seconds } → new Date(Seconds * 1000)
 *  - ISO string or epoch number             → new Date(value)
 *
 * Returns null for null / undefined / falsy values.
 */
export function timestampToDate(value: unknown): Date | null {
  const v = value as any;
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v.seconds != null)  return new Date(v.seconds  * 1000);
  if (v._seconds != null) return new Date(v._seconds * 1000);
  if (v.Seconds != null)  return new Date(v.Seconds  * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

